import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { avatarColor } from '../../lib/avatars';
import './ChannelRail.css';

export default function ChannelRail({
  rootChats,
  selectedRootChatId,
  acceptedFriends,
  onlineUserIds,
  unreadChatIds,
  unreadDmIds,
  totalInviteBadge,
  currentView,
  rootChatHasUnread,
  selectedFriend,
  onCreateRoom,
  onNavClose,
}) {
  const navigate = useNavigate();
  const [showCreateInput, setShowCreateInput] = useState(false);
  const [newRoomName, setNewRoomName] = useState('');
  const [creatingRoom, setCreatingRoom] = useState(false);
  const [createRoomError, setCreateRoomError] = useState('');

  const handleNav = (path) => {
    navigate(path);
    onNavClose?.();
  };

  const handleCreateRoom = async () => {
    const name = newRoomName.trim();
    if (!name) return;
    setCreatingRoom(true);
    setCreateRoomError('');
    const result = await onCreateRoom(name);
    setCreatingRoom(false);
    if (result?.error) {
      setCreateRoomError(result.error);
    } else {
      setNewRoomName('');
      setShowCreateInput(false);
    }
  };

  return (
    <nav className="channel-rail" aria-label="Channels">
      {/* Root channels */}
      <div className="rail-channels">
        {rootChats.map(chat => {
          const isActive = currentView === 'chats' && selectedRootChatId === chat.id;
          const hasUnread = rootChatHasUnread(chat.id);
          return (
            <button
              key={chat.id}
              className={`rail-item${isActive ? ' rail-item--active' : ''}`}
              title={chat.name}
              onClick={() => handleNav(`/channel/${chat.id}`)}
              aria-label={chat.name}
              aria-current={isActive ? 'page' : undefined}
            >
              <span className="rail-item__pill" aria-hidden="true" />
              <div
                className="rail-item__icon"
                style={{ background: chat.icon_url ? undefined : avatarColor(chat.id) }}
              >
                {chat.icon_url
                  ? <img src={chat.icon_url} alt="" />
                  : <span className="rail-item__letter">{chat.name.charAt(0).toUpperCase()}</span>
                }
              </div>
              {hasUnread && <span className="rail-item__unread-pip" aria-hidden="true" />}
            </button>
          );
        })}

        {/* Add new channel */}
        {showCreateInput ? (
          <div className="rail-create-wrap">
            <input
              className="rail-create-input"
              value={newRoomName}
              onChange={e => setNewRoomName(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') handleCreateRoom();
                if (e.key === 'Escape') { setShowCreateInput(false); setNewRoomName(''); setCreateRoomError(''); }
              }}
              placeholder="Channel name…"
              maxLength={64}
              autoFocus
            />
            {createRoomError && <p className="rail-create-error">{createRoomError}</p>}
            <div className="rail-create-actions">
              <button
                className="rail-create-confirm"
                onClick={handleCreateRoom}
                disabled={creatingRoom || !newRoomName.trim()}
              >
                Add
              </button>
              <button
                className="rail-create-cancel"
                onClick={() => { setShowCreateInput(false); setNewRoomName(''); setCreateRoomError(''); }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            className="rail-item rail-item--add"
            title="Add channel"
            onClick={() => setShowCreateInput(true)}
            aria-label="Add channel"
          >
            <span className="rail-add-icon" aria-hidden="true">+</span>
          </button>
        )}
      </div>

      <div className="rail-divider" />

      {/* Friends section */}
      <div className="rail-friends">
        <button
          className={`rail-item rail-item--icon${currentView === 'friends' && !selectedFriend ? ' rail-item--active' : ''}`}
          title="Friends"
          onClick={() => handleNav('/friends')}
          aria-label="Friends"
        >
          <span className="rail-item__pill" aria-hidden="true" />
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <circle cx="7" cy="6.5" r="3" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M1 17c0-3.3 2.7-5.5 6-5.5s6 2.2 6 5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <path d="M14 9a2.5 2.5 0 1 0 0-5M18 17c0-2.5-1.8-4.2-4-4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </button>

        {acceptedFriends.map(f => {
          const name = f.display_name || f.username || '?';
          const isActiveDm = currentView === 'dm' && selectedFriend?.id === f.id;
          return (
            <button
              key={f.friendRowId || f.id}
              className={`rail-item rail-item--sm${isActiveDm ? ' rail-item--active' : ''}`}
              title={name}
              onClick={() => handleNav(`/friends/${f.id}`)}
              aria-label={name}
            >
              <span className="rail-item__pill" aria-hidden="true" />
              <div className="rail-friend-avatar" style={{ background: avatarColor(f.id) }}>
                {name.charAt(0).toUpperCase()}
              </div>
              {onlineUserIds.has(f.id) && <span className="rail-online-dot" aria-hidden="true" />}
              {unreadDmIds.has(f.id) && <span className="rail-item__unread-pip rail-item__unread-pip--sm" aria-hidden="true" />}
            </button>
          );
        })}
      </div>

      <div className="rail-divider" />

      {/* Utilities */}
      <div className="rail-utils">
        <button
          className={`rail-item rail-item--icon${currentView === 'p2p' ? ' rail-item--active' : ''}`}
          title="P2P Chat"
          onClick={() => handleNav('/p2p')}
          aria-label="P2P Chat"
        >
          <span className="rail-item__pill" aria-hidden="true" />
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <circle cx="4" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
            <circle cx="16" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M6.5 10h7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="2 2"/>
          </svg>
        </button>

        <button
          className={`rail-item rail-item--icon${currentView === 'invites' ? ' rail-item--active' : ''}`}
          title="Invites"
          onClick={() => handleNav('/invites')}
          aria-label="Invites"
        >
          <span className="rail-item__pill" aria-hidden="true" />
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <rect x="2" y="4.5" width="16" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M2 7.5l8 5 8-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          {totalInviteBadge > 0 && (
            <span className="rail-badge" aria-label={`${totalInviteBadge} pending invites`}>
              {totalInviteBadge}
            </span>
          )}
        </button>
      </div>
    </nav>
  );
}
